/**
 * GIFT THRESHOLD SYSTEM
 * Ajoute/retire automatiquement un produit cadeau selon le montant du panier
 */

const GIFT_PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
};

// FILE D'ATTENTE GLOBALE pour coordonner les 3 instances
class GiftCoordinator {
  constructor() {
    this.queue = [];
    this.isProcessing = false;
    this.instances = [];
  }

  register(instance) {
    this.instances.push(instance);
  }

  async scheduleCheck() {
    return new Promise((resolve) => {
      this.queue.push(resolve);
      this.processQueue();
    });
  }

  async processQueue() {
    if (this.isProcessing || this.queue.length === 0) return;

    this.isProcessing = true;

    try {
      // Traiter TOUTES les instances en une seule passe
      await this.checkAllGifts();

      // Résoudre toutes les promesses en attente
      while (this.queue.length > 0) {
        const resolve = this.queue.shift();
        resolve();
      }
    } finally {
      this.isProcessing = false;

      // S'il y a encore des demandes en attente, les traiter
      if (this.queue.length > 0) {
        setTimeout(() => this.processQueue(), 0);
      }
    }
  }

  async checkAllGifts() {
    try {
      // Récupérer le panier UNE SEULE FOIS
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Calculer le total sans cadeaux
      let totalWithoutGift = 0;
      cart.items.forEach(item => {
        const isGift = item.properties && item.properties['_gift_threshold'];
        if (!isGift) {
          totalWithoutGift += item.final_line_price;
        }
      });

      // Vérifier chaque cadeau et collecter les actions nécessaires
      const actions = [];

      for (const instance of this.instances) {
        if (!instance.config || !instance.config.enabled) continue;

        const hasGift = cart.items.some(item => {
          const isGift = item.properties && item.properties['_gift_threshold'];
          const isGiftProduct = item.variant_id === instance.config.variantId;
          return isGift && isGiftProduct;
        });

        const shouldHaveGift = totalWithoutGift >= instance.config.threshold;

        if (shouldHaveGift && !hasGift) {
          actions.push({ type: 'add', instance });
        } else if (!shouldHaveGift && hasGift) {
          const giftItem = cart.items.find(item => {
            const isGift = item.properties && item.properties['_gift_threshold'];
            const isGiftProduct = item.variant_id === instance.config.variantId;
            return isGift && isGiftProduct;
          });
          actions.push({ type: 'remove', instance, giftItem });
        }
      }

      // Exécuter toutes les actions collectées
      if (actions.length > 0) {
        await this.executeActions(actions);
      }
    } catch (error) {
      console.error('Gift coordinator error:', error);
    }
  }

  async executeActions(actions) {
    // Exécuter toutes les actions en parallèle
    const promises = actions.map(action => {
      if (action.type === 'add') {
        return this.addGift(action.instance);
      } else if (action.type === 'remove') {
        return this.removeGift(action.instance, action.giftItem);
      }
    });

    await Promise.all(promises);

    // Rafraîchir le panier UNE SEULE FOIS après toutes les modifications
    await this.refreshCart();
  }

  async addGift(instance) {
    const formData = {
      id: instance.config.variantId,
      quantity: 1,
      properties: {
        '_gift_threshold': 'true',
        '_gift_text': instance.config.giftText || '🎁 CADEAU'
      }
    };

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error(`Failed to add gift: ${response.status}`);
      }
    } catch (error) {
      console.error('Error adding gift:', error);
    }
  }

  async removeGift(_instance, giftItem) {
    if (!giftItem) return;

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: giftItem.key,
          quantity: 0
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to remove gift: ${response.status}`);
      }
    } catch (error) {
      console.error('Error removing gift:', error);
    }
  }

  async refreshCart() {
    try {
      // Récupérer le panier mis à jour
      const cart = await this.getCart();

      // Utiliser le système publish/subscribe de Shopify
      if (typeof publish === 'function') {
        await publish(GIFT_PUB_SUB_EVENTS.cartUpdate, {
          source: 'gift-threshold',
          cartData: cart
        });
      } else {
        // Fallback : rafraîchir manuellement les sections
        const response = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`);
        const sectionsData = await response.json();

        // Rafraîchir le cart drawer
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          if (cartDrawer.getSectionsToRender) {
            const sectionsToRender = cartDrawer.getSectionsToRender();
            sectionsToRender.forEach((section) => {
              const elementToReplace = document.getElementById(section.id);
              const sectionData = sectionsData[section.section];

              if (elementToReplace && sectionData) {
                const html = this.getSectionInnerHTML(sectionData, section.selector);
                const targetElement = elementToReplace.querySelector(section.selector) || elementToReplace;
                targetElement.innerHTML = html;
              }
            });
          }
        }

        // Mettre à jour le badge du panier
        const cartIconBubble = document.querySelector('cart-icon-bubble');
        if (cartIconBubble && sectionsData['cart-icon-bubble']) {
          const bubbleHtml = this.getSectionInnerHTML(sectionsData['cart-icon-bubble'], '.shopify-section');
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = bubbleHtml;
          const newBubble = tempDiv.querySelector('cart-icon-bubble');
          if (newBubble) {
            cartIconBubble.innerHTML = newBubble.innerHTML;
          }
        }
      }

      // Dispatch un événement custom pour indiquer que le refresh est terminé
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { source: 'gift-coordinator' }
      }));
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }

  async getCart() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  getSectionInnerHTML(html, selector) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const element = doc.querySelector(selector);
    return element ? element.innerHTML : '';
  }
}

// Instance globale unique du coordinateur
const giftCoordinator = new GiftCoordinator();

class GiftThresholdManager {
  constructor(configName) {
    this.config = window[configName] || {};
    this.giftProperty = '_gift_threshold';

    if (!this.config.enabled || !this.config.productId || !this.config.variantId) {
      return;
    }

    // Enregistrer cette instance auprès du coordinateur
    giftCoordinator.register(this);
    this.init();
  }

  init() {
    // Vérifier au chargement
    this.checkAndUpdate();

    // Écouter les changements du panier
    document.addEventListener('cart:updated', (e) => {
      // Éviter les boucles infinies en ignorant nos propres événements
      if (e.detail && e.detail.source === 'gift-coordinator') {
        return;
      }
      this.checkAndUpdate();
    });

    // Observer les mutations du DOM pour détecter les mises à jour du panier
    if (typeof MutationObserver !== 'undefined') {
      let debounceTimer;
      const observer = new MutationObserver(() => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          this.checkAndUpdate();
        }, 300);
      });

      const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('.cart-drawer');
      if (cartDrawer) {
        observer.observe(cartDrawer, { childList: true, subtree: true });
      }
    }
  }

  async checkAndUpdate() {
    // Déléguer au coordinateur qui gérera la synchronisation
    await giftCoordinator.scheduleCheck();
  }
}

// Initialiser au chargement
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new GiftThresholdManager('themeGiftThreshold');
    new GiftThresholdManager('themeGift2Threshold');
    new GiftThresholdManager('themeGift3Threshold');
  });
} else {
  new GiftThresholdManager('themeGiftThreshold');
  new GiftThresholdManager('themeGift2Threshold');
  new GiftThresholdManager('themeGift3Threshold');
}

